import React from 'react';

import {
  Breakpoints,
  CTA,
  CustomImage,
  TextAppearance,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextTag,
  TextWeight,
} from '@vw-marketing/us-components';

import {
  ChangeVehicleCTAContainer,
  ModelCardContainer,
  ModelCardImageContainer,
  ModelCardInfoSection,
  TrimInfoContainer,
} from './styled';

import { AppConstants } from '../../utils/app-constants';
import { onVWBasicLinkClickEvent } from '../../tagging/tagging-helpers';
import { useFeatureAppConfig } from '../../hooks/use-feature-app-config';
import { useMaintenanceContext } from '../../hooks/use-maintenance-context';
import { useStore } from '../../hooks-store/store';
import { useTrackingManager } from '../../hooks/use-tracking-manager';
import IncentiveConstants from '../../hooks-store/incentive-store/actions/incentive-constants';

interface ModelCardProps {
  setLiteratureData?: (value: any) => void;
  setMaintenanceSchedule?: (value: any) => void;
}

export const ModelCard = ({
  setLiteratureData,
  setMaintenanceSchedule,
}: ModelCardProps) => {
  const { faHostServerUrl = '', mode } = useFeatureAppConfig();
  const { translationHandler } = useMaintenanceContext();
  const trackingManager = useTrackingManager();
  const [
    store,
    { [IncentiveConstants.SetModelCardData]: updateModelCardData },
  ] = useStore();
  const { currentData } = store;
  const { model, modelYear, trim, carImage } = currentData;

  const handleResetImage = () => {
    updateModelCardData({
      carImage: undefined,
    });
  };

  const handleTracking = () => {
    const link = {
      name: "Change vehicle CTA",
      url: window.location.href
    }

    onVWBasicLinkClickEvent(
      trackingManager,
      {
        Type: 'Link',
        Name: translationHandler('literature.modelCard.changeVehicle'),
        ItemCount: 1,
        ItemPosition: 1,
      },
      'Link',
      undefined, // document data if needed
      undefined, // additional tracking data
      mode,
      link
    );
  };

  return (
    <ModelCardContainer carImage={carImage}>
      <ModelCardInfoSection>
        <TextCustom
          appearance={TextAppearance.headline300}
          tag={TextTag.p}
          style={{
            [Breakpoints.default]: {
              fontSize: TextSize.TextSize24,
              fontWeight: TextWeight.bold,
              lineHeight: TextLineHeight.TextLineHeight28,
            },
            [Breakpoints.b1920]: {
              fontSize: TextSize.TextSize48,
              fontWeight: TextWeight.bold,
              lineHeight: TextLineHeight.TextLineHeight56,
            },
          }}
        >
          {modelYear} {model}
        </TextCustom>
        {trim && (
          <TrimInfoContainer>
            <TextCustom
              appearance={TextAppearance.copy200}
              tag={TextTag.p}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize14,
                  fontWeight: TextWeight.regular,
                  lineHeight: TextLineHeight.TextLineHeight24,
                },
                [Breakpoints.b1920]: {
                  fontSize: TextSize.TextSize24,
                  fontWeight: TextWeight.regular,
                  lineHeight: TextLineHeight.TextLineHeight36,
                },
              }}
            >
              {trim}
            </TextCustom>
          </TrimInfoContainer>
        )}
        <ChangeVehicleCTAContainer>
          <CTA
            tag="button"
            emphasis="tertiary"
            aria-label="Change vehicle CTA"
            onClick={() => {
              setLiteratureData && setLiteratureData([]);
              setMaintenanceSchedule && setMaintenanceSchedule(null);
              handleResetImage();
              handleTracking();
            }}
            stretchContent
          >
            <TextCustom
              appearance={TextAppearance.copy200}
              tag={TextTag.p}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize14,
                  fontWeight: TextWeight.light,
                  lineHeight: TextLineHeight.TextLineHeight24,
                },
                [Breakpoints.b1920]: {
                  fontSize: TextSize.TextSize24,
                  fontWeight: TextWeight.light,
                  lineHeight: TextLineHeight.TextLineHeight36,
                },
              }}
            >
              {translationHandler('literature.modelCard.changeVehicle')}
            </TextCustom>
          </CTA>
        </ChangeVehicleCTAContainer>
      </ModelCardInfoSection>
      {carImage && (
        <ModelCardImageContainer data-testid="car-image-section">
          <CustomImage
            src={carImage}
            alt='Vehicle_Image'
            srcErrorImage={faHostServerUrl + AppConstants.Ngw6Logo480x216}
            srcPlaceholder={AppConstants.PlaceHolder480x216 as string}
          />
        </ModelCardImageContainer>
      )}
    </ModelCardContainer>
  );
};
